import { useEffect, useRef, useState } from "react";
import { Check, Copy, RefreshCw, ShieldCheck } from "lucide-react";
import type { MindsetIntegrationDomainChallenge } from "@mindset/api-client";
import type { IntegrationDomainClient } from "./integrationSetupClient";

interface Props {
  companyId: string; userId: string; applicationId: string; origin: string; client: IntegrationDomainClient;
  onChanged?: () => void;
}
export function IntegrationDomain(props: Props) {
  return <Domain key={JSON.stringify([props.companyId, props.userId, props.applicationId, props.origin])} {...props} />;
}
function Domain({ companyId, userId, applicationId, origin, client, onChanged }: Props) {
  const [challenge, setChallenge] = useState<MindsetIntegrationDomainChallenge | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");
  const [replace, setReplace] = useState(false);
  const [loading, setLoading] = useState(Boolean(client.currentDomain));
  const [recoveryFailed, setRecoveryFailed] = useState(false);
  const [recoveryVersion, setRecoveryVersion] = useState(0);
  const key = useRef<string | null>(null);
  const running = useRef(false);
  const alive = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const recover = client.currentDomain;
  useEffect(() => {
    if (!recover) return;
    let current = true;
    setLoading(true); setRecoveryFailed(false); setError("");
    void recover(companyId, userId, applicationId, origin).then((result) => {
      if (!current) return;
      setChallenge(result);
      const expired = Boolean(result) && Date.parse(result!.ownership?.validUntil ?? result!.expiresAt) <= Date.now();
      setReplace(Boolean(expired) || result?.state === "REVOKED");
      setStatus(!result ? "Not verified" : result.state === "REVOKED" ? "Verification revoked" : expired ? "Verification expired" : result.state === "VERIFIED" ? "Domain verified" : "Awaiting DNS verification");
    }, () => {
      if (current) { setRecoveryFailed(true); setError("Saved domain verification could not be loaded."); }
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [recover, companyId, userId, applicationId, origin, recoveryVersion]);
  useEffect(() => {
    if (!challenge || challenge.state === "REVOKED") return;
    const expire = () => { setReplace(true); setStatus("Verification expired"); };
    const remaining = Date.parse(challenge.ownership?.validUntil ?? challenge.expiresAt) - Date.now();
    if (remaining <= 0) { expire(); return; }
    const timer = setTimeout(expire, Math.min(remaining, 2147483647));
    return () => clearTimeout(timer);
  }, [challenge]);
  useEffect(() => {
    if (!recover || challenge?.state !== "VERIFIED") return;
    const timer = setTimeout(() => setRecoveryVersion((version) => version + 1), 60000);
    return () => clearTimeout(timer);
  }, [recover, challenge, recoveryVersion]);
  async function run() {
    if (running.current || loading) return;
    if (recoveryFailed) { setRecoveryVersion((value) => value + 1); return; }
    running.current = true; setBusy(true); setError("");
    try {
      if (!challenge || replace) {
        key.current ??= crypto.randomUUID();
        const result = await client.issueDomain(companyId, userId, applicationId, origin, key.current);
        if (!alive.current) return;
        setChallenge(result); key.current = null; setReplace(false); setCopied("");
        onChanged?.();
        setStatus(result.state === "VERIFIED" ? "Domain verified" : result.state === "REVOKED" ? "Challenge replaced" : "Awaiting DNS verification");
        if (result.state === "REVOKED") setReplace(true);
      } else {
        const result = await client.verifyDomain(companyId, userId, challenge.id);
        if (!alive.current) return;
        setChallenge(result.challenge);
        if (result.status === "verified") onChanged?.();
        const messages = { verified: "Domain verified", pending: "DNS record not found yet", expired: "Challenge expired", revoked: "Challenge replaced",
          unavailable: "DNS lookup unavailable. Try again.", aborted: "Verification interrupted. Try again." };
        setStatus(messages[result.status]);
        setReplace(result.status === "expired" || result.status === "revoked");
      }
    } catch {
      if (alive.current) setError("Verification could not be confirmed. Retry the same request.");
    } finally { running.current = false; if (alive.current) setBusy(false); }
  }
  async function copy(label: string, value: string) {
    try { await navigator.clipboard.writeText(value); if (alive.current) { setCopied(label); setError(""); } }
    catch { if (alive.current) setError("Clipboard unavailable. Select and copy the record value."); }
  }
  return <section className="integration-domain" aria-label={`Domain verification for ${origin}`}>
    <header><strong>{origin}</strong><span role="status">{loading ? "Loading verification..." : recoveryFailed ? "Status unavailable" : status || "Not verified"}</span></header>
    {challenge ? <dl>
      {([["Type", challenge.instructions.type], ["Name", challenge.instructions.name], ["Value", challenge.instructions.value]] as const).map(([label, value]) =>
        <div key={label}><dt>{label}</dt><dd><code>{value}</code><button type="button" className="portal-icon-button" title={`Copy DNS ${label.toLowerCase()}`} aria-label={copied === label ? `DNS ${label.toLowerCase()} copied` : `Copy DNS ${label.toLowerCase()}`} onClick={() => void copy(label, value)}>{copied === label ? <Check size={16} /> : <Copy size={16} />}</button></dd></div>)}
      <div><dt>{challenge.ownership ? "Ownership valid until" : "Challenge expires"}</dt><dd><time dateTime={challenge.ownership?.validUntil ?? challenge.expiresAt}>{new Date(challenge.ownership?.validUntil ?? challenge.expiresAt).toLocaleString()}</time></dd></div>
    </dl> : null}
    {error ? <p role="alert">{error}</p> : null}
    <button type="button" className="portal-button" disabled={busy || loading || (!recoveryFailed && challenge?.state === "VERIFIED" && !replace)} onClick={() => void run()}>
      {busy ? <RefreshCw size={16} className="spin" /> : <ShieldCheck size={16} />}
      {loading ? "Loading verification..." : recoveryFailed ? "Reload verification" : busy ? "Checking..." : replace ? "New DNS challenge" : challenge?.state === "VERIFIED" ? "Verified" : challenge ? "Verify DNS" : "Get DNS record"}
    </button>
  </section>;
}
